import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import ContentCutOutlinedIcon from '@mui/icons-material/ContentCutOutlined';
import { useTranslation } from 'react-i18next';
import BrandThemeProvider from '../../theme/BrandThemeProvider';
import { BRAND, BRAND_RADIUS } from '../../theme/brandTokens';
import {
  STORE_IMAGE_CATEGORY_CUSTOMER_LOGO,
} from '../../constants/storeImageCategories';

const findImage = (images, category) => (images || []).find((image) => (
  image?.category === category
  || String(image?.category).toLowerCase() === String(category).toLowerCase()
));

const PreviewLogo = ({ imageUrl, storeName }) => (
  imageUrl ? (
    <Box
      component="img"
      src={imageUrl}
      alt={storeName || undefined}
      title={storeName || undefined}
      sx={{
        display: 'block',
        width: 'auto',
        maxWidth: '72%',
        height: { xs: 36, sm: 42 },
        objectFit: 'contain',
        objectPosition: 'left center',
      }}
    />
  ) : (
    <Typography
      sx={{
        minWidth: 0,
        maxWidth: '72%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontWeight: 700,
        color: BRAND.text,
      }}
    >
      {storeName}
    </Typography>
  )
);

const PreviewCustomerView = ({ storeName, customerLogoUrl, t }) => (
  <Box data-testid="store-branding-preview-customer" sx={{ background: BRAND.background, color: BRAND.text, minHeight: 300 }}>
    <Box
      data-testid="store-branding-preview-customer-header"
      style={{ backgroundColor: BRAND.surface, color: BRAND.text }}
      sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${BRAND.line}` }}
    >
      <PreviewLogo imageUrl={customerLogoUrl} storeName={storeName || t('stores.branding.preview.storeName')} />
      <Box sx={{ mt: 1, display: 'flex', gap: 1.5 }}>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: BRAND.accentText }}>
          {t('stores.branding.preview.bookButton')}
        </Typography>
        <Typography sx={{ fontSize: '0.7rem', color: BRAND.textMuted }}>
          {t('stores.branding.preview.menuItem')}
        </Typography>
      </Box>
    </Box>
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
        {t('stores.branding.preview.customerTitle')}
      </Typography>
      <Box
        sx={{
          background: BRAND.surface,
          border: `1px solid ${BRAND.line}`,
          borderRadius: BRAND_RADIUS.button,
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <ContentCutOutlinedIcon fontSize="small" sx={{ color: BRAND.accentText }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
            {t('stores.branding.preview.serviceName')}
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: BRAND.textMuted }}>
            {t('stores.branding.preview.serviceMeta')}
          </Typography>
        </Box>
        <Box sx={{ background: BRAND.accent, color: BRAND.onAccent, borderRadius: BRAND_RADIUS.pill, px: 1, py: 0.35, fontSize: '0.65rem', fontWeight: 700 }}>
          {t('stores.branding.preview.selected')}
        </Box>
      </Box>
      <Box sx={{ alignSelf: 'flex-start', background: BRAND.accent, color: BRAND.onAccent, borderRadius: BRAND_RADIUS.button, px: 1.5, py: 0.75, fontSize: '0.75rem', fontWeight: 700 }}>
        {t('stores.branding.preview.bookButton')}
      </Box>
    </Box>
  </Box>
);

/** Customer booking-page preview themed with the draft colours and customer logo. */
const StoreBrandingPreview = ({ colors, storeName, images = [] }) => {
  const { t } = useTranslation();
  const customerLogoUrl = useMemo(
    () => findImage(images, STORE_IMAGE_CATEGORY_CUSTOMER_LOGO)?.url,
    [images],
  );

  return (
    <BrandThemeProvider colors={colors} scope="local">
      <Box
        id="store-branding-preview"
        sx={{
          borderRadius: BRAND_RADIUS.card,
          border: `1px solid ${BRAND.line}`,
          overflow: 'hidden',
        }}
      >
        <PreviewCustomerView
          storeName={storeName}
          customerLogoUrl={customerLogoUrl}
          t={t}
        />
      </Box>
    </BrandThemeProvider>
  );
};

export default StoreBrandingPreview;
